import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { api } from '@/api/client';
import type { components } from '@/api/schema';
import { AppIcon } from '@/components/AppIcon';
import { FarmPicker } from '@/components/FarmPicker';
import {
  ActionBar,
  DataGrid,
  DetailPane,
  SplitView,
  StatusBar,
  type DesktopAction,
  type DetailTab,
  type GridColumn,
} from '@/components/desktop';
import { HistoryPanel } from '@/components/documents';
import {
  DialogSubject,
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  Modal,
  OkNote,
  Pager,
  SearchBar,
  Select,
  TextArea,
  errorMessage,
  shortDate,
  todayIso,
} from '@/components/ui';
import { formatCount, formatQuantity } from '@/format';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { farmNameKey } from '@/components/farmMatch';
import { SearchSelect } from '@/components/SearchSelect';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';

type Flock = components['schemas']['FlockDto'];
type FlockDetail = components['schemas']['FlockDetailDto'];
type FlockDay = components['schemas']['FlockDayDto'];
type Shed = components['schemas']['ShedDto'];
type Farm = components['schemas']['FarmDto'];
type FlockClash = components['schemas']['FlockClashDto'];

const PAGE_SIZE = 50;

type Status = 'live' | 'closed' | 'all';

/** One breed in a shed, and how many of it went in. */
interface BreedLine {
  key: number;
  breed: string;
  qty: string;
}

/** A shed card on the new-flock form: the shed, and the breeds in it. */
interface PlacementLine {
  key: number;
  /** A shed on the farm, or `NEW_SHED` while `shedName` names a new one. */
  shedId: string;
  /** The name of a shed not on the farm yet, added with the flock. */
  shedName: string;
  breeds: BreedLine[];
}

/** Stands in for the id of a shed typed in as new, until saving creates it. */
const NEW_SHED = 'new';

/** The form of a shed name two sheds on a farm may not share — the server's
 *  rule in `common/name-key.ts`, which farm and shed names share. */
const shedNameKey = farmNameKey;

let lineKey = 0;
function emptyBreed(): BreedLine {
  return { key: ++lineKey, breed: '', qty: '' };
}
function emptyLine(): PlacementLine {
  return { key: ++lineKey, shedId: '', shedName: '', breeds: [emptyBreed()] };
}

/** What a card's shed is called, for a message about it. */
function shedLabel(line: PlacementLine, shed: { code: string } | undefined): string {
  if (shed) return shed.code;
  return line.shedId === NEW_SHED && line.shedName.trim() ? `“${line.shedName.trim()}”` : 'A shed';
}

/** The same per-line limit the server applies. */
const MAX_BIRDS = 1_000_000;

/** A breed's name as the server stores it: spaces collapsed. */
function tidyBreed(name: string): string {
  return name.trim().replace(/\s+/g, ' ');
}

/**
 * The form of a breed's name two lines in one shed may not share — the
 * server's rule: case, spacing and punctuation ignored, so "Ross 308" and
 * "ross-308" are one breed.
 */
function breedKey(name: string): string {
  return name.normalize('NFKC').toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');
}

function breedStarted(b: BreedLine): boolean {
  return b.breed.trim() !== '' || b.qty.trim() !== '';
}

function birdsOf(b: BreedLine): number {
  return Number(b.qty) || 0;
}

function shedTotal(breeds: readonly BreedLine[]): number {
  return breeds.reduce((n, b) => n + birdsOf(b), 0);
}

/**
 * Why one breed line cannot be saved as it is, or null. `certain` marks a
 * problem worth showing while still typing — a breed twice, a count no shed
 * holds — rather than one that may only mean the line is half-filled.
 */
function breedLineProblem(b: BreedLine, all: readonly BreedLine[]): { text: string; certain: boolean } | null {
  if (!breedStarted(b)) return null;
  const name = tidyBreed(b.breed);
  const key = breedKey(name);
  const first = all.find((o) => breedStarted(o) && key !== '' && breedKey(o.breed) === key);
  if (first && first !== b) return { text: `${name} is listed twice. Put all of its birds on one line.`, certain: true };
  if (!name) return { text: 'Which breed are these birds?', certain: false };
  const n = Number(b.qty);
  if (b.qty.trim() === '' || !(n > 0)) return { text: `How many ${name} went in?`, certain: false };
  if (!Number.isInteger(n)) return { text: `Birds are counted whole: ${name} cannot be ${b.qty}.`, certain: true };
  if (n > MAX_BIRDS) return { text: `${formatCount(n)} is more than any shed holds. Check the ${name} count.`, certain: true };
  return null;
}

/** Why a shed's breed lines cannot be saved as they are, or null. */
function shedProblem(breeds: readonly BreedLine[], capacity: number | null | undefined): string | null {
  const started = breeds.filter(breedStarted);
  if (started.length === 0) return 'Add a breed and how many birds of it went in.';
  for (const b of started) {
    const problem = breedLineProblem(b, breeds);
    if (problem) return problem.text;
  }
  const total = shedTotal(started);
  if (capacity && total > capacity) {
    return `This shed holds ${formatCount(capacity)}; ${formatCount(total)} are entered — ${formatCount(total - capacity)} too many.`;
  }
  return null;
}

/** The breed lines worth sending: tidied, and blank ones dropped. */
function breedsToSend(breeds: readonly BreedLine[]) {
  return breeds.filter(breedStarted).map((b) => ({ breed: tidyBreed(b.breed), qty: Number(b.qty) }));
}

/** Breeds already placed, plus any typed on this form, for the breed boxes to offer. */
function useBreedSuggestions(typed: readonly string[]): string[] {
  const known = useQuery({
    queryKey: ['flocks', 'breeds'],
    queryFn: () => api.get('/api/v1/flocks/breeds'),
    staleTime: 5 * 60_000,
  });
  const out = new Map<string, string>();
  const listed = Array.isArray(known.data) ? (known.data as Array<{ breed: string }>) : [];
  for (const s of listed) out.set(breedKey(s.breed), s.breed);
  for (const t of typed) {
    const name = tidyBreed(t);
    if (breedKey(name) && !out.has(breedKey(name))) out.set(breedKey(name), name);
  }
  return [...out.values()];
}

/** "Ross 308 2,000 · Cobb 500 14,000" — a flock's breed mix. */
function breedMixLabel(breeds: ReadonlyArray<{ breed: string; qty: number }>): string {
  return breeds.map((b) => `${b.breed} ${formatCount(b.qty)}`).join(' · ');
}

/** A number, or an em dash where there is genuinely nothing to show. */
function orDash(value: string | null): string {
  return value ?? '—';
}

/**
 * Flocks: a batch of birds on a farm, from placement to clearance.
 *
 * Every figure here is worked out by the server from the daily register and
 * the stock issues behind it, so nothing on this screen can disagree with the
 * entries that explain it. A dash means "not known yet" and never zero —
 * an FCR of 0.000 would read as a perfect cycle.
 */
export function Flocks() {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [status, setStatus] = useState<Status>('live');
  const [search, setSearch] = useState('');
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [tab, setTab] = useState('summary');
  const [note, setNote] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [closing, setClosing] = useState<FlockDetail | null>(null);
  const [addingShed, setAddingShed] = useState<FlockDetail | null>(null);

  const list = useQuery({
    queryKey: ['flocks', 'list', farmIds, status, search, skip],
    queryFn: () =>
      api.get('/api/v1/flocks', {
        take: PAGE_SIZE,
        skip,
        status,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...farmScopeQuery(farmIds),
      }),
  });

  const detail = useQuery({
    queryKey: ['flocks', 'detail', selectedKey],
    queryFn: async () =>
      (await api.get(`/api/v1/flocks/${selectedKey}` as '/api/v1/flocks/{id}')) as unknown as FlockDetail,
    enabled: selectedKey !== null,
  });

  const days = useQuery({
    queryKey: ['flocks', 'days', selectedKey],
    queryFn: async () =>
      (await api.get(`/api/v1/flocks/${selectedKey}/days` as '/api/v1/flocks/{id}/days')) as unknown as FlockDay[],
    enabled: selectedKey !== null && tab === 'days',
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['flocks'] });
    // The shed list carries which flock is in each shed, and the farm list
    // counts flocks, so both go stale the moment a flock changes.
    void queryClient.invalidateQueries({ queryKey: ['sheds'] });
    void queryClient.invalidateQueries({ queryKey: ['farms'] });
  }

  const reopen = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/api/v1/flocks/${id}/reopen`, { reason }),
    onSuccess: () => {
      refresh();
      setNote('Reopened. Remember to close it again once it is right.');
    },
  });

  const retire = useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/flocks/${id}`),
    onSuccess: (result: unknown) => {
      refresh();
      setSelectedKey(null);
      setNote((result as { message?: string }).message ?? 'Done.');
    },
  });

  const rows = (list.data?.items ?? []) as Flock[];
  const selected = rows.find((r) => r.id === selectedKey) ?? null;

  const columns: Array<GridColumn<Flock>> = [
    {
      key: 'code',
      header: 'Code',
      width: 110,
      render: (f) => (
        <span className="withicon">
          <AppIcon kind="flock" size={14} />
          <span className="mono">{f.code}</span>
        </span>
      ),
    },
    { key: 'farm', header: 'Farm', width: 110, render: (f) => <span className="dim">{f.farmCode}</span> },
    {
      key: 'sheds',
      header: 'Sheds',
      width: 150,
      render: (f) => <span className="dim">{f.shedCodes.join(', ') || '—'}</span>,
    },
    {
      key: 'breeds',
      header: 'Breeds',
      width: 140,
      render: (f) =>
        f.breeds.length === 0 ? (
          '—'
        ) : (
          <span title={breedMixLabel(f.breeds)}>
            {f.breeds[0]!.breed}
            {f.breeds.length > 1 && <span className="dim"> +{f.breeds.length - 1}</span>}
          </span>
        ),
    },
    { key: 'placed', header: 'Placed on', width: 100, render: (f) => shortDate(f.placementDate) },
    {
      key: 'age',
      header: 'Age',
      align: 'end',
      width: 80,
      render: (f) => (f.closedDate ? <span className="dim">{f.ageDays} days</span> : `Day ${f.ageDays}`),
    },
    { key: 'placedQty', header: 'Birds placed', align: 'end', width: 110, render: (f) => formatCount(f.birdsPlaced) },
    { key: 'alive', header: 'Birds alive', align: 'end', width: 110, render: (f) => formatCount(f.birdsAlive) },
    {
      key: 'mortality',
      header: 'Mortality',
      align: 'end',
      width: 95,
      render: (f) => (f.mortalityPct === null ? '—' : `${f.mortalityPct}%`),
    },
    {
      key: 'feed',
      header: 'Feed used',
      align: 'end',
      width: 110,
      render: (f) => <>{formatQuantity(f.feedUsedKg, 0)} <span className="dim">kg</span></>,
    },
    {
      key: 'weight',
      header: 'Avg weight',
      align: 'end',
      width: 105,
      render: (f) => (f.avgWeightG === null ? '—' : <>{formatCount(f.avgWeightG)} <span className="dim">g</span></>),
    },
    { key: 'fcr', header: 'FCR', align: 'end', width: 75, render: (f) => orDash(f.fcr) },
    {
      key: 'status',
      header: 'Status',
      width: 90,
      render: (f) =>
        f.closedDate ? <span className="dim">Closed</span> : <span className="pill pill--ok">Live</span>,
    },
  ];

  function rowActions(row: Flock): DesktopAction[] {
    const live = row.closedDate === null;
    return [
      {
        label: 'Add shed',
        icon: 'shed' as const,
        onSelect: () => void openAddShed(row.id),
        allowed: can('mdm.flock.update'),
        enabled: live,
        reason: 'Reopen the flock first',
      },
      {
        label: 'Close flock',
        icon: 'post' as const,
        onSelect: () => void openClose(row.id),
        allowed: can('mdm.flock.update'),
        enabled: live,
        reason: 'This flock is already closed',
      },
      {
        label: 'Reopen',
        icon: 'repeat' as const,
        onSelect: () => {
          const reason = window.prompt('Why is this flock being reopened?');
          if (reason?.trim()) reopen.mutate({ id: row.id, reason: reason.trim() });
        },
        allowed: can('mdm.flock.update'),
        enabled: !live,
        reason: 'This flock is still live',
        separatorBefore: true,
      },
      {
        label: 'Remove',
        icon: 'remove' as const,
        onSelect: () => {
          if (window.confirm(`Remove ${row.code}? A flock with any history is switched off instead of deleted.`)) {
            retire.mutate(row.id);
          }
        },
        allowed: can('mdm.flock.delete'),
      },
    ];
  }

  async function openClose(id: string) {
    setClosing((await api.get(`/api/v1/flocks/${id}` as '/api/v1/flocks/{id}')) as unknown as FlockDetail);
  }

  async function openAddShed(id: string) {
    setAddingShed((await api.get(`/api/v1/flocks/${id}` as '/api/v1/flocks/{id}')) as unknown as FlockDetail);
  }

  const toolbarActions: DesktopAction[] = [
    {
      label: 'New flock',
      icon: 'flock',
      primary: true,
      onSelect: () => setComposing(true),
      allowed: can('mdm.flock.create'),
    },
  ];

  const d = detail.data;
  const tabs: DetailTab[] = d
    ? [
        { id: 'summary', label: 'Summary', icon: 'details', content: <FlockSummary flock={d} /> },
        { id: 'sheds', label: 'Sheds', icon: 'shed', content: <FlockSheds flock={d} /> },
        {
          id: 'days',
          label: 'Day by day',
          icon: 'attendance',
          content: <FlockDays days={days.data ?? []} loading={days.isPending} />,
        },
        { id: 'history', label: 'History', icon: 'history', content: <HistoryPanel path={`/api/v1/flocks/${d.id}/history`} /> },
      ]
    : [];

  const totals = rows.reduce(
    (t, f) => ({ alive: t.alive + f.birdsAlive, placed: t.placed + f.birdsPlaced }),
    { alive: 0, placed: 0 },
  );

  if (composing) {
    return (
      <NewFlock
        onCancel={() => setComposing(false)}
        onSaved={(message) => {
          setComposing(false);
          refresh();
          setNote(message);
        }}
      />
    );
  }

  return (
    <div className="workbench">
      <SplitView
        id="flocks"
        direction="column"
        initial={380}
        firstLabel="flock list"
        first={
          <div className="workbench__main">
            <ActionBar actions={toolbarActions}>
              <SearchBar
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setSkip(0);
                }}
                placeholder="Search flocks by code or name"
              />
              <Select
                label="Show"
                value={status}
                onChange={(v) => {
                  setStatus(v as Status);
                  setSkip(0);
                }}
                options={[
                  { value: 'live', label: 'Live' },
                  { value: 'closed', label: 'Closed' },
                  { value: 'all', label: 'All' },
                ]}
                placeholder=""
              />
            </ActionBar>

            {note && <OkNote message={note} />}
            {(reopen.error ?? retire.error) && (
              <ErrorNote message={errorMessage(reopen.error ?? retire.error, 'Action failed')} />
            )}
            {list.error && <ErrorNote message={list.error.message} />}

            <DataGrid
              id="flocks"
              label="Flocks"
              columns={columns}
              rows={rows}
              rowKey={(f) => f.id}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              actions={rowActions}
              loading={list.isPending}
              empty={
                status === 'live'
                  ? 'No live flocks. Place one to start a cycle.'
                  : 'No flocks to show.'
              }
            />
            <Pager skip={skip} take={PAGE_SIZE} total={list.data?.total ?? 0} onChange={setSkip} />
          </div>
        }
        second={
          <DetailPane
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            empty="Choose a flock to see its figures, its sheds and its day-by-day register."
            title={
              selected && (
                <span className="withicon">
                  <AppIcon kind="flock" size={14} />
                  <span className="mono">{selected.code}</span> {selected.nameEn}
                </span>
              )
            }
          />
        }
      />

      <StatusBar>
        <span>{rows.length === 1 ? '1 flock' : `${rows.length} flocks`}</span>
        <span>{formatCount(totals.alive)} birds alive</span>
        <span>of {formatCount(totals.placed)} placed</span>
      </StatusBar>

      {closing && (
        <CloseFlock
          flock={closing}
          onClose={() => setClosing(null)}
          onSaved={(message) => {
            setClosing(null);
            refresh();
            setNote(message);
          }}
        />
      )}
      {addingShed && (
        <AddShed
          flock={addingShed}
          onClose={() => setAddingShed(null)}
          onSaved={(message) => {
            setAddingShed(null);
            refresh();
            setNote(message);
          }}
        />
      )}
    </div>
  );
}

function FlockSummary({ flock }: { flock: FlockDetail }) {
  return (
    <div className="stack">
      <dl className="detaillist">
        <dt>Status</dt>
        <dd>{flock.closedDate ? `Closed on ${shortDate(flock.closedDate)}` : `Live — day ${flock.ageDays}`}</dd>
        <dt>Farm</dt>
        <dd>{flock.farmCode} — {flock.farmName}</dd>
        <dt>Placed on</dt>
        <dd>{shortDate(flock.placementDate)}</dd>
        <dt>{flock.breeds.length > 1 ? 'Breeds' : 'Breed'}</dt>
        <dd>{flock.breeds.length === 0 ? '—' : breedMixLabel(flock.breeds)}</dd>
        <dt>Hatchery</dt>
        <dd>{flock.hatchery ?? '—'}</dd>

        <dt>Birds placed</dt>
        <dd>{formatCount(flock.birdsPlaced)}</dd>
        <dt>Birds alive</dt>
        <dd>{formatCount(flock.birdsAlive)}</dd>
        <dt>Dead · culled</dt>
        <dd>{formatCount(flock.dead)} · {formatCount(flock.culled)}</dd>
        <dt>Mortality</dt>
        <dd>{flock.mortalityPct === null ? '—' : `${flock.mortalityPct}%`}</dd>
        <dt>Lost in the last 7 days</dt>
        <dd>{formatCount(flock.lostLast7)}</dd>

        <dt>Feed issued</dt>
        <dd>{formatQuantity(flock.feedIssuedKg, 0)} kg</dd>
        <dt>Feed used</dt>
        <dd>{formatQuantity(flock.feedUsedKg, 0)} kg</dd>
        <dt>Feed still in the shed</dt>
        <dd>
          {formatQuantity(flock.feedInShedKg, 0)} kg
          {Number(flock.feedInShedKg) < 0 && (
            <span className="dim"> — more was used than issued, so an issue may be missing</span>
          )}
        </dd>
        <dt>Feed per bird placed</dt>
        <dd>{flock.feedPerBirdKg === null ? '—' : `${flock.feedPerBirdKg} kg`}</dd>

        <dt>Average weight</dt>
        <dd>{flock.avgWeightG === null ? 'No sample taken yet' : `${formatCount(flock.avgWeightG)} g`}</dd>
        <dt>Live weight</dt>
        <dd>{flock.liveWeightKg === null ? '—' : `${formatQuantity(flock.liveWeightKg, 0)} kg`}</dd>
        <dt>FCR</dt>
        <dd>
          {orDash(flock.fcr)}
          {flock.fcr === null && <span className="dim"> — needs feed issued and a weight</span>}
        </dd>

        {flock.closedDate && (
          <>
            <dt>Birds sold</dt>
            <dd>{flock.soldBirds === null ? '—' : formatCount(flock.soldBirds)}</dd>
            <dt>Weight sold</dt>
            <dd>{flock.soldWeightKg === null ? '—' : `${formatQuantity(flock.soldWeightKg, 0)} kg`}</dd>
            <dt>Remarks</dt>
            <dd>{flock.closeRemarks ?? '—'}</dd>
          </>
        )}
        <dt>Last register entry</dt>
        <dd>{flock.lastEntryOn ? shortDate(flock.lastEntryOn) : 'Nothing entered yet'}</dd>
      </dl>
    </div>
  );
}

function FlockSheds({ flock }: { flock: FlockDetail }) {
  if (flock.sheds.length === 0) return <p className="muted">This flock has no sheds.</p>;

  return (
    <table className="table">
      <thead>
        <tr>
          <th>Shed</th>
          <th className="num">Capacity</th>
          <th className="num">Birds placed</th>
          <th className="num">Dead</th>
          <th className="num">Culled</th>
          <th className="num">Birds alive</th>
          <th>Filled on</th>
          <th>Cleared on</th>
        </tr>
      </thead>
      <tbody>
        {flock.sheds.map((s) => (
          <ShedRows key={s.placementId} shed={s} />
        ))}
      </tbody>
    </table>
  );
}

/**
 * A shed's line on the Sheds tab, and under it one line per breed when it
 * holds more than one. A shed of one breed names it under the shed instead.
 */
function ShedRows({ shed: s }: { shed: FlockDetail['sheds'][number] }) {
  return (
    <>
      <tr>
        <td>
          <span className="mono">{s.shedCode}</span> {s.shedName}
          {s.breeds.length === 1 && <div className="dim">{s.breeds[0]!.breed}</div>}
        </td>
        <td className="num dim">{s.capacity === null ? '—' : formatCount(s.capacity)}</td>
        <td className="num">{formatCount(s.placedQty)}</td>
        <td className="num">{formatCount(s.dead)}</td>
        <td className="num">{formatCount(s.culled)}</td>
        <td className="num">{formatCount(s.birdsAlive)}</td>
        <td>{shortDate(s.placedOn)}</td>
        <td className="dim">{s.removedOn ? shortDate(s.removedOn) : '—'}</td>
      </tr>
      {s.breeds.length > 1 &&
        s.breeds.map((b) => (
          <tr key={b.id} className="table__breedrow">
            <td>{b.breed}</td>
            <td />
            <td className="num">{formatCount(b.placedQty)}</td>
            <td className="num">{formatCount(b.dead)}</td>
            <td className="num">{formatCount(b.culled)}</td>
            <td className="num">{formatCount(b.birdsAlive)}</td>
            <td />
            <td />
          </tr>
        ))}
    </>
  );
}

function FlockDays({ days, loading }: { days: FlockDay[]; loading: boolean }) {
  if (loading) return <p className="muted">Loading…</p>;
  if (days.length === 0) {
    return <p className="muted">Nothing entered yet. Fill in the Daily Register under Daily Operations.</p>;
  }

  return (
    <table className="table">
      <thead>
        <tr>
          <th className="num">Day</th>
          <th>Date</th>
          <th className="num">Dead</th>
          <th className="num">Culled</th>
          <th className="num">Feed</th>
          <th className="num">Avg weight</th>
          <th className="num">Birds alive</th>
          <th className="num">Sheds</th>
        </tr>
      </thead>
      <tbody>
        {days.map((d) => (
          <tr key={String(d.date)}>
            <td className="num">{d.dayNo}</td>
            <td>{shortDate(d.date)}</td>
            <td className="num">{formatCount(d.dead)}</td>
            <td className="num">{formatCount(d.culled)}</td>
            <td className="num">{formatQuantity(d.feedKg, 0)} <span className="dim">kg</span></td>
            <td className="num dim">{d.avgWeightG === null ? '—' : `${formatCount(d.avgWeightG)} g`}</td>
            <td className="num">{formatCount(d.birdsAlive)}</td>
            <td className="num dim">{d.shedCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NewFlock({ onCancel, onSaved }: { onCancel: () => void; onSaved: (message: string) => void }) {
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const { can } = useAuth();

  const [farmId, setFarmId] = useState('');
  const [code, setCode] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [hatchery, setHatchery] = useState('');
  const [placementDate, setPlacementDate] = useState(todayIso());
  const [lines, setLines] = useState<PlacementLine[]>([emptyLine()]);
  const [problem, setProblem] = useState<string | null>(null);
  // Problems on a card show as they are typed only where they are certain (a
  // breed twice, a shed overfilled); the rest wait for a save, so a card
  // half-way through being filled in is not already in red.
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  // Deleted farms come too, only so typing one's code or name says it is
  // deleted rather than missing. Only active farms can be chosen.
  const farms = useQuery({
    queryKey: ['farms', 'list', true],
    queryFn: () => api.get('/api/v1/farms', { includeInactive: 'true' }),
  });
  const sheds = useQuery({ queryKey: ['sheds', 'all'], queryFn: () => api.get('/api/v1/sheds') });

  const scopedFarms = (farms.data as Farm[] | undefined)?.filter(
    (f) => farmIds.length === 0 || farmIds.includes(f.id),
  );
  const allSheds = (sheds.data ?? []) as Shed[];

  // Only this farm's empty sheds: a shed already holding a live flock cannot
  // take another, and offering it would only produce a refusal on save.
  const shedOptions = allSheds.filter((s) => s.farmId === farmId && s.flockId === null);
  const occupied = allSheds.filter((s) => s.farmId === farmId && s.flockId !== null);
  // Every shed on the farm, for the names a new one may not take.
  const farmSheds = allSheds.filter((s) => s.farmId === farmId);
  // A shed typed in by name is added for real, so it asks for the permission
  // that adding one in Farm Manager asks for.
  const canAddShed = can('mdm.shed.create');

  function emptyShedsOn(farm: { id: string }): string {
    const n = allSheds.filter((s) => s.farmId === farm.id && s.flockId === null).length;
    return n === 0 ? 'No empty sheds' : `${n} empty ${n === 1 ? 'shed' : 'sheds'}`;
  }

  function chooseFarm(id: string) {
    if (id === farmId) return;
    setFarmId(id);
    // Sheds belong to a farm, so lines typed against the old one no longer
    // mean anything.
    setLines([emptyLine()]);
  }

  // Ask the server whether the typed code is taken, once typing pauses. It
  // applies the same rule as on save: "21" is taken when FLK-0021 exists,
  // closed and retired flocks included. A code of all zeros is refused here
  // without asking.
  const typedCode = code.trim();
  const zeroCode = /^0+$/.test(typedCode);
  const checkedCode = useDebouncedValue(typedCode);
  const codeCheck = useQuery({
    queryKey: ['flocks', 'code-check', checkedCode],
    queryFn: () => api.get('/api/v1/flocks/code-check', { code: checkedCode }),
    enabled: checkedCode !== '' && !/^0+$/.test(checkedCode),
    staleTime: 0,
  });
  const codeSettled = checkedCode === typedCode && codeCheck.isSuccess;
  const codeTaken = codeSettled ? codeCheck.data.clash : undefined;
  // The lowest number nobody holds, offered while the box is empty.
  const suggestion = useQuery({
    queryKey: ['flocks', 'code-check', ''],
    queryFn: () => api.get('/api/v1/flocks/code-check'),
    staleTime: 0,
  }).data?.suggested;

  // The same for the name, which no two flocks on one farm may share. The
  // server ignores case, spacing and punctuation, and counts closed flocks.
  const typedName = nameEn.trim();
  const checkedName = useDebouncedValue(typedName);
  const nameCheck = useQuery({
    queryKey: ['flocks', 'name-check', farmId, checkedName.toLowerCase()],
    queryFn: () => api.get('/api/v1/flocks/name-check', { farmId, name: checkedName }),
    enabled: farmId !== '' && checkedName !== '',
    staleTime: 0,
  });
  const nameTaken = farmId && checkedName === typedName && nameCheck.isSuccess ? nameCheck.data.clash : undefined;

  const started = lines.filter((l) => l.shedId || l.shedName.trim() || l.breeds.some(breedStarted));
  const totalBirds = started.reduce((n, l) => n + shedTotal(l.breeds), 0);
  const shedOf = (id: string) => allSheds.find((s) => s.id === id);
  const suggestions = useBreedSuggestions(lines.flatMap((l) => l.breeds.map((b) => b.breed)));

  // Birds of each breed across every shed, for the line under the cards.
  const mix = new Map<string, { breed: string; qty: number }>();
  for (const l of started) {
    for (const b of l.breeds) {
      const name = tidyBreed(b.breed);
      if (!breedKey(name) || !(birdsOf(b) > 0)) continue;
      const seen = mix.get(breedKey(name));
      if (seen) seen.qty += birdsOf(b);
      else mix.set(breedKey(name), { breed: name, qty: birdsOf(b) });
    }
  }
  const breedTotals = [...mix.values()].sort((a, b) => b.qty - a.qty);
  // A new card is only worth adding while there is an empty shed for it.
  const shedsLeft = shedOptions.filter((s) => !lines.some((l) => l.shedId === s.id)).length;
  const blankCards = lines.filter((l) => !l.shedId).length;
  // With no empty shed left, another card is still worth adding when the user
  // may type a new shed's name onto it.
  const canAddCard = canAddShed || shedsLeft > blankCards;

  function setLine(key: number, patch: Partial<PlacementLine>) {
    setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function validate(): string | null {
    if (!farmId) return 'Choose the farm these birds went to.';
    if (!typedCode) return 'Type a flock code, up to 6 digits.';
    if (zeroCode) return 'A flock code cannot be all zeros.';
    if (codeTaken) {
      return `Flock code ${typedCode} is already used by ${codeTaken.code} (${codeTaken.nameEn}). Use a different code.`;
    }
    if (!typedName) return 'Give the flock a name, so people can tell cycles apart.';
    if (nameTaken) {
      return `${nameTaken.code} on this farm is already named “${nameTaken.nameEn}”${flockState(nameTaken)}. Use a different name.`;
    }
    if (started.length === 0) return 'Add at least one shed, with the breeds that went into it.';
    if (started.some((l) => !l.shedId)) {
      return canAddShed
        ? 'Every card with birds on it needs a shed chosen, or a name for a new one.'
        : 'Every card with birds on it needs a shed chosen.';
    }
    if (started.some((l) => l.shedId === NEW_SHED && !l.shedName.trim())) {
      return 'A new shed needs a name.';
    }
    const ids = started.filter((l) => l.shedId !== NEW_SHED).map((l) => l.shedId);
    if (new Set(ids).size !== ids.length) {
      return 'A shed is listed twice. Put all of its breeds on one card.';
    }
    const newNames = started
      .filter((l) => l.shedId === NEW_SHED)
      .map((l) => shedNameKey(l.shedName));
    if (new Set(newNames).size !== newNames.length) {
      return 'The same new shed is named twice. Put all of its breeds on one card.';
    }
    const taken = started.find(
      (l) => l.shedId === NEW_SHED && farmSheds.some((s) => shedNameKey(s.nameEn) === shedNameKey(l.shedName)),
    );
    if (taken) {
      return `This farm already has a shed named “${taken.shedName.trim()}”. Choose it instead, or use a different name.`;
    }
    for (const l of started) {
      const shed = shedOf(l.shedId);
      const issue = shedProblem(l.breeds, shed?.capacity);
      if (issue) return `${shedLabel(l, shed)}: ${issue}`;
    }
    return null;
  }

  async function submit() {
    const issue = validate();
    setProblem(issue);
    setTried(true);
    if (issue) return;

    try {
      setSaving(true);
      const result = (await api.post('/api/v1/flocks', {
        farmId,
        // The server checks the code and name again inside the insert's
        // transaction, so two people typing the same one cannot both save it.
        code: typedCode,
        nameEn: typedName,
        ...(hatchery.trim() ? { hatchery: hatchery.trim() } : {}),
        placementDate,
        placements: started.map((l) => ({
          ...(l.shedId === NEW_SHED ? { shedName: l.shedName.trim() } : { shedId: l.shedId }),
          breeds: breedsToSend(l.breeds),
        })),
      })) as { code?: string };
      onSaved(`Flock ${result.code ?? ''} placed with ${formatCount(totalBirds)} birds.`);
    } catch (e) {
      setProblem(errorMessage(e, 'Could not place the flock'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <FormPanel
      icon={<AppIcon kind="flock" size={18} />}
      title="New flock"
      intro="A batch of chicks placed on one farm. Spread it across as many sheds as it fills, with a line for each breed in a shed — birds placed is the total of the lines."
    >
      <FormGroup title="The flock" template="minmax(0, 1.7fr) minmax(9rem, 0.6fr) minmax(0, 1.4fr)">
        <FarmPicker value={farmId} onChange={chooseFarm} farms={scopedFarms} detail={emptyShedsOn} required />
        <Field
          label="Flock code"
          required
          mono
          numeric
          maxLength={6}
          value={code}
          onChange={(v) => setCode(v.replace(/\D/g, '').slice(0, 6))}
          placeholder={suggestion ? `e.g. ${suggestion}` : ''}
          invalid={!!codeTaken || zeroCode}
          hint={
            typedCode === '' ? (
              <>
                Up to 6 digits. Fixed once saved.
                {suggestion && (
                  <>
                    {' '}
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => setCode(suggestion)}>
                      Use {suggestion}
                    </button>
                  </>
                )}
              </>
            ) : zeroCode ? (
              <span style={{ color: 'var(--c-alert)' }}>Cannot be all zeros</span>
            ) : codeTaken ? (
              <FlockTakenHint flock={codeTaken} />
            ) : codeSettled ? (
              'Available'
            ) : (
              'Checking…'
            )
          }
        />
        <Field
          label="Name"
          required
          maxLength={160}
          value={nameEn}
          onChange={setNameEn}
          placeholder="Okara 21 — October"
          invalid={!!nameTaken}
          hint={
            nameTaken ? (
              <FlockTakenHint flock={nameTaken} where="on this farm" />
            ) : !farmId && typedName ? (
              'Checked against the farm’s flocks once you choose one'
            ) : (
              'No two flocks on one farm may share a name'
            )
          }
        />
        <Field label="Placed on" type="date" required value={placementDate} onChange={setPlacementDate} />
        <Field label="Hatchery" value={hatchery} onChange={setHatchery} span={2} />
      </FormGroup>

      <FormGroup
        title="Sheds"
        hint={
          !farmId
            ? 'Choose a farm first.'
            : shedOptions.length === 0
              ? canAddShed
                ? 'Every shed on this farm already holds a live flock. Close one first, or type a name to add a shed.'
                : 'Every shed on this farm already holds a live flock. Close one first.'
              : occupied.length > 0
                ? `One card per shed, a line per breed in it. ${occupied.length} shed(s) on this farm already hold a live flock and are not offered.`
                : 'One card per shed, a line per breed in it.'
        }
      >
        <div className="shedcards">
          {lines.map((line, i) => {
            const shed = shedOf(line.shedId);
            const total = shedTotal(line.breeds);
            const over = shed?.capacity ? total - shed.capacity : 0;
            return (
              <section
                key={line.key}
                className="shedcard"
                aria-label={shed ? `Shed ${shed.code}` : line.shedId === NEW_SHED && line.shedName ? `Shed ${line.shedName}` : `Shed ${i + 1}`}
              >
                <div className="shedcard__head">
                  <SearchSelect
                    label="Shed"
                    value={line.shedId}
                    selectedLabel={line.shedId === NEW_SHED ? line.shedName : undefined}
                    onChange={(option) =>
                      setLine(line.key, { shedId: option?.value ?? '', shedName: '' })
                    }
                    options={shedOptions
                      .filter((s) => s.id === line.shedId || !lines.some((l) => l.shedId === s.id))
                      .map((s) => ({
                        value: s.id,
                        label: s.capacity ? `${s.code} — ${s.nameEn} (holds ${formatCount(s.capacity)})` : `${s.code} — ${s.nameEn}`,
                        keywords: s.nameUr ?? '',
                      }))}
                    disabled={!farmId}
                    placeholder={farmId ? 'Search by code or name…' : 'Choose a farm first'}
                    commitExact
                    clearable
                    emptyText={(typed) =>
                      shedOptions.length === 0
                        ? canAddShed
                          ? 'No empty shed on this farm. Type a name to add one.'
                          : 'No empty shed on this farm'
                        : canAddShed
                          ? `No empty shed matches “${typed}”. Type it again and choose “+ Add” to add it as a new shed.`
                          : `No empty shed matches “${typed}”`
                    }
                    {...(canAddShed && farmId
                      ? {
                          onCreate: (name: string) =>
                            setLine(line.key, { shedId: NEW_SHED, shedName: name.trim() }),
                          createLabel: (name: string) => `+ Add “${name}” as a new shed`,
                        }
                      : {})}
                  />
                  <p className={`shedcard__total${over > 0 ? ' shedcard__total--over' : ''}`} aria-live="polite">
                    <strong>{formatCount(total)}</strong> birds
                    {shed?.capacity ? <span className="dim"> of {formatCount(shed.capacity)}</span> : null}
                    {over > 0 && <span> — {formatCount(over)} over what it holds</span>}
                  </p>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setLines((ls) => (ls.length === 1 ? [emptyLine()] : ls.filter((l) => l.key !== line.key)))}
                    aria-label={shed ? `Remove shed ${shed.code}` : 'Remove this shed'}
                    disabled={!farmId}
                  >
                    Remove shed
                  </button>
                </div>
                {line.shedId === NEW_SHED && (
                  <p className="settings__hint shedcard__new" role="status">
                    <span className="pill">New</span> Added to this farm when you save, and listed in Farm Manager
                    for review, to fill in its capacity and the rest.
                  </p>
                )}
                <BreedRows
                  breeds={line.breeds}
                  onChange={(breeds) => setLine(line.key, { breeds })}
                  suggestions={suggestions}
                  disabled={!farmId}
                  showProblems={tried}
                />
              </section>
            );
          })}
        </div>
        <div className="shedcards__foot">
          <button
            type="button"
            className="btn"
            onClick={() => setLines((ls) => [...ls, emptyLine()])}
            disabled={!farmId || !canAddCard}
            title={farmId && !canAddCard ? 'Every empty shed on this farm is already on the form' : undefined}
          >
            Add another shed
          </button>
          <span>
            Birds placed: <strong>{formatCount(totalBirds)}</strong>
            {started.length > 1 && <span className="dim"> in {started.length} sheds</span>}
          </span>
          {breedTotals.length > 1 && (
            <span className="shedcards__mix" aria-label="Birds of each breed">
              {breedTotals.map((b) => (
                <span key={b.breed} className="pill">
                  {b.breed} · {formatCount(b.qty)}
                </span>
              ))}
            </span>
          )}
        </div>
      </FormGroup>

      {problem && <ErrorNote message={problem} />}

      <FormActions onCancel={onCancel} cancelLabel="Back to list">
        <button type="button" className="btn btn--primary" disabled={saving} onClick={() => void submit()}>
          Place flock
        </button>
      </FormActions>
    </FormPanel>
  );
}

/** " (closed)" or " (retired)" after a flock's name, when it is either. */
function flockState(flock: { closed: boolean; isActive: boolean }): string {
  return flock.closed ? ' (closed)' : flock.isActive ? '' : ' (retired)';
}

/** Names the flock already using a typed code or name, so the user can tell whether it is the one they meant. */
function FlockTakenHint({ flock, where }: { flock: FlockClash; where?: string }) {
  return (
    <span style={{ color: 'var(--c-alert)' }}>
      Already used{where ? ` ${where}` : ''} by {flock.code} — {flock.nameEn}
      {flockState(flock)}
    </span>
  );
}

/** The flock a dialog acts on, with the live bird count it changes. */
function FlockSubject({ flock }: { flock: FlockDetail }) {
  return (
    <DialogSubject
      icon="flock"
      code={flock.code}
      name={flock.nameEn}
      nameUr={flock.nameUr}
      tags={[
        { icon: 'farm', label: `${flock.farmCode} · ${flock.farmName}` },
        flock.shedCodes.length > 0 ? { icon: 'shed', label: flock.shedCodes.join(', ') } : null,
        { icon: 'month', label: `Day ${flock.ageDays}` },
      ]}
      figure={{ label: 'Birds alive', value: formatCount(flock.birdsAlive) }}
    />
  );
}

function CloseFlock({
  flock,
  onClose,
  onSaved,
}: {
  flock: FlockDetail;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const [closedDate, setClosedDate] = useState(todayIso());
  const [soldBirds, setSoldBirds] = useState(String(flock.birdsAlive));
  const [soldWeightKg, setSoldWeightKg] = useState('');
  const [remarks, setRemarks] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit() {
    setProblem(null);
    try {
      setSaving(true);
      await api.post(`/api/v1/flocks/${flock.id}/close`, {
        closedDate,
        ...(soldBirds.trim() ? { soldBirds: Number(soldBirds) } : {}),
        ...(soldWeightKg.trim() ? { soldWeightKg: soldWeightKg.trim() } : {}),
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
      });
      onSaved(`${flock.code} closed. Its sheds are free for the next flock.`);
    } catch (e) {
      setProblem(errorMessage(e, 'Could not close the flock'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Close the flock" onClose={onClose} medium>
      <h2 className="withicon">
        <AppIcon kind="retire" size={18} />
        Close the flock
      </h2>
      <FlockSubject flock={flock} />
      <p className="muted">
        Closing frees this flock&rsquo;s {flock.sheds.length === 1 ? 'shed' : `${flock.sheds.length} sheds`} for the next
        placement, and makes the flock read-only.
      </p>
      <FormGroup title="Clearance" columns={2}>
        <Field label="Closed on" type="date" required value={closedDate} onChange={setClosedDate} />
        <Field
          label="Birds sold"
          type="number"
          min={0}
          value={soldBirds}
          onChange={setSoldBirds}
          hint="Cannot be more than the birds alive"
        />
        <Field
          label="Total weight sold (kg)"
          type="number"
          min={0}
          value={soldWeightKg}
          onChange={setSoldWeightKg}
          hint="Used for the final FCR"
        />
        <TextArea label="Remarks" value={remarks} onChange={setRemarks} />
      </FormGroup>

      {problem && <ErrorNote message={problem} />}

      <FormActions onCancel={onClose}>
        <button type="button" className="btn btn--primary" disabled={saving} onClick={() => void submit()}>
          Close flock
        </button>
      </FormActions>
    </Modal>
  );
}

/**
 * A shed's breed lines: the breed, typed with suggestions, and its birds.
 * Enter in the last line's birds starts the next breed.
 */
function BreedRows({
  breeds,
  onChange,
  suggestions,
  disabled = false,
  showProblems,
}: {
  breeds: BreedLine[];
  onChange: (breeds: BreedLine[]) => void;
  suggestions: string[];
  disabled?: boolean;
  /** Mark every line that cannot be saved, not only the certain ones. */
  showProblems: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  // Focus follows an added or removed line, so the keyboard never has to go
  // looking for where it went. Held until the line has been drawn.
  const focusKey = useRef<number | null>(null);

  useEffect(() => {
    if (focusKey.current === null) return;
    box.current?.querySelector<HTMLInputElement>(`[data-breed="${focusKey.current}"] input`)?.focus();
    focusKey.current = null;
  });

  function set(key: number, patch: Partial<BreedLine>) {
    onChange(breeds.map((b) => (b.key === key ? { ...b, ...patch } : b)));
  }

  function add() {
    const next = emptyBreed();
    onChange([...breeds, next]);
    focusKey.current = next.key;
  }

  function remove(key: number) {
    // The last line is cleared rather than removed: a shed always has one.
    if (breeds.length === 1) {
      const next = emptyBreed();
      onChange([next]);
      focusKey.current = next.key;
      return;
    }
    const at = breeds.findIndex((b) => b.key === key);
    const rest = breeds.filter((b) => b.key !== key);
    onChange(rest);
    focusKey.current = rest[Math.min(at, rest.length - 1)]!.key;
  }

  function onBirdsKey(e: KeyboardEvent<HTMLDivElement>, b: BreedLine) {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (b === breeds[breeds.length - 1] && breedStarted(b)) add();
  }

  return (
    <div className="breedrows" ref={box}>
      {breeds.map((b, i) => {
        const problem = breedLineProblem(b, breeds);
        const shown = problem !== null && (showProblems || problem.certain);
        const name = tidyBreed(b.breed);
        const nameAtFault = shown && (!name || problem.text.includes('twice'));
        return (
          <div key={b.key} className="breedrows__row" data-breed={b.key}>
            <Field
              label={breeds.length === 1 ? 'Breed' : `Breed ${i + 1}`}
              value={b.breed}
              onChange={(breed) => set(b.key, { breed })}
              placeholder={i === 0 ? 'e.g. Ross 308' : 'Another breed'}
              // Breeds already on another line of this shed are not offered.
              suggestions={suggestions.filter(
                (s) => breedKey(s) === breedKey(b.breed) || !breeds.some((o) => breedKey(o.breed) === breedKey(s)),
              )}
              maxLength={120}
              invalid={nameAtFault}
              disabled={disabled}
            />
            <div onKeyDown={(e) => onBirdsKey(e, b)}>
              <Field
                label="Birds"
                type="number"
                min={1}
                max={MAX_BIRDS}
                value={b.qty}
                onChange={(qty) => set(b.key, { qty })}
                invalid={shown && !nameAtFault}
                disabled={disabled}
              />
            </div>
            <button
              type="button"
              className="btn btn--ghost btn--sm breedrows__remove"
              onClick={() => remove(b.key)}
              aria-label={name ? `Remove ${name}` : 'Remove this breed'}
              title={breeds.length === 1 ? 'Clear this line' : 'Remove this breed'}
              disabled={disabled}
            >
              ✕
            </button>
            {shown && <p className="breedrows__problem">{problem.text}</p>}
          </div>
        );
      })}
      <div>
        <button type="button" className="btn btn--ghost btn--sm" onClick={add} disabled={disabled}>
          + Add breed
        </button>
      </div>
    </div>
  );
}

function AddShed({
  flock,
  onClose,
  onSaved,
}: {
  flock: FlockDetail;
  onClose: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const [shedId, setShedId] = useState('');
  const [shedName, setShedName] = useState('');
  const [breeds, setBreeds] = useState<BreedLine[]>(() => [emptyBreed()]);
  const [placedOn, setPlacedOn] = useState(todayIso());
  const [problem, setProblem] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);

  const sheds = useQuery({ queryKey: ['sheds', 'all'], queryFn: () => api.get('/api/v1/sheds') });
  const farmSheds = ((sheds.data ?? []) as Shed[]).filter((s) => s.farmId === flock.farmId);
  const options = farmSheds.filter((s) => s.flockId === null);
  const shed = options.find((s) => s.id === shedId);
  // Adding a shed by name here adds it for real, as Farm Manager does.
  const canAddShed = can('mdm.shed.create');
  const newShed = shedId === NEW_SHED;
  // The flock's own breeds are offered too: birds moved to another shed are
  // usually more of the same.
  const suggestions = useBreedSuggestions([...flock.breeds.map((b) => b.breed), ...breeds.map((b) => b.breed)]);
  const total = shedTotal(breeds);
  const over = shed?.capacity ? total - shed.capacity : 0;

  async function submit() {
    setTried(true);
    setProblem(null);
    if (!shedId) {
      return setProblem(canAddShed ? 'Choose the shed, or type a name to add one.' : 'Choose the shed.');
    }
    if (newShed && !shedName.trim()) return setProblem('A new shed needs a name.');
    if (newShed && farmSheds.some((s) => shedNameKey(s.nameEn) === shedNameKey(shedName))) {
      return setProblem(`This farm already has a shed named “${shedName.trim()}”. Choose it instead, or use a different name.`);
    }
    const issue = shedProblem(breeds, shed?.capacity);
    if (issue) return setProblem(issue);
    try {
      setSaving(true);
      await api.post(`/api/v1/flocks/${flock.id}/placements`, {
        ...(newShed ? { shedName: shedName.trim() } : { shedId }),
        breeds: breedsToSend(breeds),
        placedOn,
      });
      onSaved(`${formatCount(total)} birds added to ${flock.code}.`);
    } catch (e) {
      setProblem(errorMessage(e, 'Could not add the shed'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title="Add a shed" onClose={onClose} medium>
      <h2 className="withicon">
        <AppIcon kind="shed" size={18} />
        Add a shed
      </h2>
      <FlockSubject flock={flock} />
      <p className="muted">
        For a flock still being filled, or birds moved into another shed. Birds placed goes up by what you enter here.
      </p>
      <FormGroup title="Shed" columns={2}>
        <SearchSelect
          label="Shed"
          required
          value={shedId}
          selectedLabel={newShed ? shedName : undefined}
          onChange={(option) => {
            setShedId(option?.value ?? '');
            setShedName('');
          }}
          options={options.map((s) => ({
            value: s.id,
            label: s.capacity ? `${s.code} — ${s.nameEn} (holds ${formatCount(s.capacity)})` : `${s.code} — ${s.nameEn}`,
            keywords: s.nameUr ?? '',
          }))}
          placeholder="Search by code or name…"
          commitExact
          clearable
          emptyText={(typed) =>
            options.length === 0
              ? canAddShed
                ? 'Every other shed on this farm already holds a live flock. Type a name to add one.'
                : 'Every other shed on this farm already holds a live flock.'
              : canAddShed
                ? `No empty shed matches “${typed}”. Type it again and choose “+ Add” to add it as a new shed.`
                : `No empty shed matches “${typed}”`
          }
          {...(canAddShed
            ? {
                onCreate: (name: string) => {
                  setShedId(NEW_SHED);
                  setShedName(name.trim());
                },
                createLabel: (name: string) => `+ Add “${name}” as a new shed`,
              }
            : {})}
          hint={
            newShed ? (
              <span>
                <span className="pill">New</span> Added to this farm when you save, and listed in Farm Manager for
                review.
              </span>
            ) : options.length === 0 && !canAddShed ? (
              'Every other shed on this farm already holds a live flock.'
            ) : undefined
          }
        />
        <Field label="Filled on" type="date" required value={placedOn} onChange={setPlacedOn} />
      </FormGroup>
      <FormGroup
        title="Breeds"
        hint={
          <span className={over > 0 ? 'shedcard__total--over' : undefined}>
            {formatCount(total)} birds
            {shed?.capacity ? ` of the ${formatCount(shed.capacity)} this shed holds` : ''}
            {over > 0 && ` — ${formatCount(over)} over`}
          </span>
        }
      >
        <BreedRows breeds={breeds} onChange={setBreeds} suggestions={suggestions} showProblems={tried} />
      </FormGroup>

      {problem && <ErrorNote message={problem} />}

      <FormActions onCancel={onClose}>
        <button type="button" className="btn btn--primary" disabled={saving} onClick={() => void submit()}>
          Add shed
        </button>
      </FormActions>
    </Modal>
  );
}
